import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { IconButton, Tooltip, TextField, CircularProgress } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import { updateReportingParticularsRoutine } from 'ducks/reporting/actions'
import { selectReportParticulars, selectIsUpdatingParticulars } from 'ducks/reporting/selectors'

const FIELDS = [
  { key: 'seller',            label: 'Seller' },
  { key: 'buyer',             label: 'Buyer' },
  { key: 'property',          label: 'Property' },
  { key: 'transaction',       label: 'Transaction' },
  { key: 'sellerSolicitors',  label: "Seller's solicitors" },
  { key: 'buyerSolicitors',   label: "Buyer's solicitors" },
  { key: 'date',              label: 'Date' },
]

const ParticularsCard = ({ bpId, reportId }) => {
  const dispatch = useDispatch()
  const particulars = useSelector(selectReportParticulars)
  const isSaving = useSelector(selectIsUpdatingParticulars)

  const [editMode, setEditMode] = useState(false)
  const [draft, setDraft] = useState({})

  useEffect(() => {
    if (!editMode) {
      setDraft(particulars || {})
    }
  }, [particulars, editMode])

  const handleEdit = () => {
    setDraft(particulars || {})
    setEditMode(true)
  }

  const handleCancel = () => {
    setEditMode(false)
    setDraft(particulars || {})
  }

  const handleSave = () => {
    dispatch(updateReportingParticularsRoutine.trigger({ bpId, reportId, particulars: draft }))
    setEditMode(false)
  }

  const handleFieldChange = (key) => (e) => {
    setDraft(prev => ({ ...prev, [key]: e.target.value }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Particulars</CardTitle>
        <CardActions>
          {isSaving && <CircularProgress size={16} />}
          {!editMode && !isSaving && (
            <Tooltip title="Edit particulars">
              <IconButton size="small" onClick={handleEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {editMode && (
            <>
              <Tooltip title="Save">
                <IconButton size="small" onClick={handleSave} disabled={isSaving}>
                  <SaveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </CardActions>
      </CardHeader>
      <CardBody>
        {FIELDS.map(({ key, label }) => (
          <Row key={key}>
            <RowLabel>{label}:</RowLabel>
            {editMode ? (
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={draft[key] || ''}
                onChange={handleFieldChange(key)}
              />
            ) : (
              <RowValue empty={!particulars?.[key]}>
                {particulars?.[key] || '(not set)'}
              </RowValue>
            )}
          </Row>
        ))}
      </CardBody>
    </Card>
  )
}

export default ParticularsCard

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  overflow: hidden;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}08, transparent);
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CardBody = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const RowLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[200]};
  min-width: 140px;
`

const RowValue = styled.span`
  font-size: 14px;
  color: ${({ empty, theme }) => empty ? theme.colors.grey[200] : theme.colors.haiti};
  font-style: ${({ empty }) => empty ? 'italic' : 'normal'};
`
